import { ReactNode, createContext, useContext, useMemo, useReducer } from 'react';
import produce from 'immer';

import { OnboardingStepType, OnboardingInputType } from '@account_module/types/Onboarding';
import {
  ONBOARDING_FORM_KEYS,
  MEGAPHONE_ONBOARDING_STEP_KEYS,
} from '@account_module/constants/onBoardingKeys';
import { unimplemented } from '@utils/commonUtils';

type OnboardingContextKeys =
  | 'account.onboarding.current_step'
  | 'account.onboarding.is_sidebar_visible'
  | 'account.onboarding.selected_input'
  | 'account.onboarding.networks_to_include'
  | 'account.onboarding.networks_to_exclude'
  | 'account.onboarding.shows_to_exclude'
  | 'account.onboarding.shows_to_exclude'
  | 'account.onboarding.swap_lists'
  | 'account.onboarding.is_invite_modal_visible'
  | 'account.onboarding.organization_id'
  | 'account.onboarding.token'
  | 'account.onboarding.integration_id'
  | 'account.onboarding.persisted_onboarding_progress';

type MegaphoneOnboardContextType = {
  integrationId: number | null;
  token: string | null;
  organizationId: string | null;
  isSidebarVisible: boolean;
  currentStep: OnboardingStepType;
  selectedInput: OnboardingInputType;
  networksToInclude: string[];
  networksToExclude: string[];
  showsToInclude: string[];
  showsToExclude: string[];
  isInviteModalVisible: boolean;
  persistedProgressStep: number;
  setCurrentStep: (step: OnboardingStepType) => void;
  setIsSidebarVisible: (val: boolean) => void;
  setSelectedInput: (val: OnboardingInputType) => void;
  setNetworksToInclude: (val: string) => void;
  setNetworksToExclude: (val: string) => void;
  setShowsToInclude: (val: string[]) => void;
  setShowsToExclude: (val: string[]) => void;
  swapLists: () => void;
  setIsInviteModalVisible: (val: boolean) => void;
  setOrganizationId: (val: string | null) => void;
  setToken: (val: string | null) => void;
  setIntegrationId: (val: number | null) => void;
  setPersistedProgressStep: (val: number) => void;
};

type ActionType = {
  type: OnboardingContextKeys;
  payload: string | number | string[] | boolean | OnboardingStepType | OnboardingInputType | null;
};

const ONBOARDING_CONTEXT_KEYS = {
  CURRENT_STEP: 'account.onboarding.current_step' as OnboardingContextKeys,
  IS_SIDEBAR_VISIBLE: 'account.onboarding.is_sidebar_visible' as OnboardingContextKeys,
  SELECTED_INPUT: 'account.onboarding.selected_input' as OnboardingContextKeys,
  ONBOARDING_PROGRESS: 'account.onboarding.onboarding_progress' as OnboardingContextKeys,
  NETWORKS_TO_INCLUDE: 'account.onboarding.networks_to_include' as OnboardingContextKeys,
  NETWORKS_TO_EXCLUDE: 'account.onboarding.networks_to_exclude' as OnboardingContextKeys,
  SHOWS_TO_INCLUDE: 'account.onboarding.shows_to_include' as OnboardingContextKeys,
  SHOWS_TO_EXCLUDE: 'account.onboarding.shows_to_exclude' as OnboardingContextKeys,
  SWAP_LISTS: 'account.onboarding.swap_lists' as OnboardingContextKeys,
  IS_INVITE_MODAL_VISIBLE: 'account.onboarding.is_invite_modal_visible' as OnboardingContextKeys,
  ORGANIZATION_ID: 'account.onboarding.organization_id' as OnboardingContextKeys,
  TOKEN: 'account.onboarding.token' as OnboardingContextKeys,
  INTEGRATION_ID: 'account.onboarding.integration_id' as OnboardingContextKeys,
  PERSISTED_ONBOARDING_STEP:
    'account.onboarding.persisted_onboarding_progress' as OnboardingContextKeys,
};

const initialState: MegaphoneOnboardContextType = {
  token: null,
  integrationId: null,
  organizationId: null,
  selectedInput: ONBOARDING_FORM_KEYS.API_TOKEN,
  currentStep: MEGAPHONE_ONBOARDING_STEP_KEYS.VERIFY_CREDENTIALS,
  isSidebarVisible: false,
  networksToInclude: [],
  networksToExclude: [],
  showsToInclude: [],
  showsToExclude: [],
  isInviteModalVisible: false,
  persistedProgressStep: 0,
  setOrganizationId: unimplemented,
  setToken: unimplemented,
  setCurrentStep: unimplemented,
  setIsSidebarVisible: unimplemented,
  setSelectedInput: unimplemented,
  setNetworksToInclude: unimplemented,
  setNetworksToExclude: unimplemented,
  setShowsToInclude: unimplemented,
  setShowsToExclude: unimplemented,
  swapLists: unimplemented,
  setIsInviteModalVisible: unimplemented,
  setIntegrationId: unimplemented,
  setPersistedProgressStep: unimplemented,
};

const reducer = produce((draft: MegaphoneOnboardContextType, action: ActionType) => {
  const { type, payload } = action;

  switch (type) {
    case ONBOARDING_CONTEXT_KEYS.IS_SIDEBAR_VISIBLE:
      draft.isSidebarVisible = payload as boolean;
      break;

    case ONBOARDING_CONTEXT_KEYS.CURRENT_STEP:
      draft.currentStep = payload as OnboardingStepType;
      break;

    case ONBOARDING_CONTEXT_KEYS.SELECTED_INPUT:
      draft.selectedInput = payload as OnboardingInputType;
      break;

    case ONBOARDING_CONTEXT_KEYS.NETWORKS_TO_INCLUDE: {
      if (!draft.networksToInclude.includes(payload as string)) {
        draft.networksToInclude = [...draft.networksToInclude, payload as string];
        draft.networksToExclude = draft.networksToExclude.filter((id: string) => id !== payload);
      }
      break;
    }

    case ONBOARDING_CONTEXT_KEYS.NETWORKS_TO_EXCLUDE: {
      if (!draft.networksToExclude.includes(payload as string)) {
        draft.networksToExclude = [...draft.networksToExclude, payload as string];
        draft.networksToInclude = draft.networksToInclude.filter((id: string) => id !== payload);
      }
      break;
    }

    case ONBOARDING_CONTEXT_KEYS.SHOWS_TO_INCLUDE: {
      draft.showsToInclude = payload as string[];
      break;
    }

    case ONBOARDING_CONTEXT_KEYS.SHOWS_TO_EXCLUDE: {
      draft.showsToExclude = payload as string[];

      break;
    }

    case ONBOARDING_CONTEXT_KEYS.SWAP_LISTS: {
      const toInclude = draft.networksToInclude;
      const toExclude = draft.networksToExclude;

      draft.networksToExclude = toInclude;
      draft.networksToInclude = toExclude;
      break;
    }

    case ONBOARDING_CONTEXT_KEYS.IS_INVITE_MODAL_VISIBLE: {
      draft.isInviteModalVisible = payload as boolean;

      break;
    }

    case ONBOARDING_CONTEXT_KEYS.ORGANIZATION_ID: {
      draft.organizationId = payload as string;

      break;
    }

    case ONBOARDING_CONTEXT_KEYS.TOKEN: {
      draft.token = payload as string;

      break;
    }

    case ONBOARDING_CONTEXT_KEYS.INTEGRATION_ID: {
      draft.integrationId = payload as number | null;

      break;
    }

    case ONBOARDING_CONTEXT_KEYS.PERSISTED_ONBOARDING_STEP: {
      draft.persistedProgressStep = payload as number;

      break;
    }
  }
});

export const MegaphoneOnboardContext = createContext<MegaphoneOnboardContextType>(initialState);

export const MegaphoneOnboardProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setCurrentStep = (value: OnboardingStepType) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.CURRENT_STEP, payload: value });
  };

  const setIsSidebarVisible = (value: boolean) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.IS_SIDEBAR_VISIBLE, payload: value });
  };

  const setSelectedInput = (value: OnboardingInputType) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.SELECTED_INPUT, payload: value });
  };

  const setNetworksToInclude = (value: string) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.NETWORKS_TO_INCLUDE, payload: value });
  };

  const setNetworksToExclude = (value: string) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.NETWORKS_TO_EXCLUDE, payload: value });
  };

  const setShowsToInclude = (value: string[]) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.SHOWS_TO_INCLUDE, payload: value });
  };

  const setShowsToExclude = (value: string[]) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.SHOWS_TO_EXCLUDE, payload: value });
  };

  const swapLists = () => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.SWAP_LISTS, payload: null });
  };

  const setIsInviteModalVisible = (value: boolean) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.IS_INVITE_MODAL_VISIBLE, payload: value });
  };

  const setOrganizationId = (value: string | null) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.ORGANIZATION_ID, payload: value });
  };

  const setToken = (value: string | null) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.TOKEN, payload: value });
  };

  const setIntegrationId = (value: number | null) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.INTEGRATION_ID, payload: value });
  };

  const setPersistedProgressStep = (value: number) => {
    return dispatch({ type: ONBOARDING_CONTEXT_KEYS.PERSISTED_ONBOARDING_STEP, payload: value });
  };

  const values = useMemo(
    () => ({
      ...state,
      setCurrentStep,
      setIsSidebarVisible,
      setSelectedInput,
      setNetworksToInclude,
      setNetworksToExclude,
      setShowsToInclude,
      setShowsToExclude,
      swapLists,
      setIsInviteModalVisible,
      setOrganizationId,
      setToken,
      setIntegrationId,
      setPersistedProgressStep,
    }),
    [state, setCurrentStep, setIsSidebarVisible]
  );

  return (
    <MegaphoneOnboardContext.Provider value={values}>{children}</MegaphoneOnboardContext.Provider>
  );
};

export const useMegaphoneOnboardContext = () => useContext(MegaphoneOnboardContext);
